import React, { useState } from 'react';
import { Card, Row, Col, Badge, Button, ButtonGroup, Form } from 'react-bootstrap';
import { FaShieldAlt, FaCog, FaBolt, FaDollarSign, FaLeaf, FaLock, FaFilePdf } from 'react-icons/fa';

function ReportDetailView({ report, onBack }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [isExporting, setIsExporting] = useState(false);
  
  // Safety check for report structure
  if (!report || !report.aggregatedReport || !report.aggregatedReport.executiveSnapshot) {
    return (
      <div style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
        <div className="container-fluid" style={{ paddingTop: '80px', paddingLeft: '30px', paddingRight: '30px' }}>
          <Button onClick={onBack} variant="outline-secondary" size="sm" className="mb-3">
            ← Back
          </Button>
          <Card className="text-center py-5">
            <Card.Body>
              <h5 className="text-danger">Report Data Error</h5>
              <p className="text-muted">The report data is incomplete or in an unexpected format.</p>
              <pre className="text-start" style={{ fontSize: '12px', backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '4px', maxHeight: '300px', overflow: 'auto' }}>
                {JSON.stringify(report, null, 2)}
              </pre>
            </Card.Body>
          </Card>
        </div>
      </div>
    );
  }
  
  const agg = report.aggregatedReport;
  const executiveSnapshot = agg.executiveSnapshot || {};

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      const tenantID = localStorage.getItem('tenantID');
      const API_URL = 'https://hiyg5j9ysa.execute-api.us-west-2.amazonaws.com/prod';
      
      const response = await fetch(
        `${API_URL}/review/${tenantID}/${report.accountId}/reports/${report.reportId}/pdf`,
        {
          headers: {
            'Accept': 'application/pdf'
          }
        }
      );
      
      if (!response.ok) throw new Error('PDF generation failed');
      
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `WAF-Report-${report.accountId}-${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('PDF export failed:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const getScoreColor = (score) => {
    if (!score) return '#6c757d';
    if (score >= 90) return '#28a745';
    if (score >= 75) return '#17a2b8';
    if (score >= 60) return '#342ead';
    if (score >= 40) return '#ffc107';
    return '#dc3545';
  };

  const getPillarIcon = (pillar) => {
    const icons = {
      'Security': <FaLock />,
      'Operational Excellence': <FaCog />,
      'Reliability': <FaShieldAlt />,
      'Performance Efficiency': <FaBolt />,
      'Cost Optimization': <FaDollarSign />,
      'Sustainability': <FaLeaf />
    };
    return icons[pillar] || null;
  };

  return (
    <div style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <div className="container-fluid" style={{ paddingTop: '80px', paddingLeft: '30px', paddingRight: '30px' }}>
        {/* Header */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex align-items-center gap-3">
              <Button onClick={onBack} variant="outline-secondary" size="sm">
                ← Back
              </Button>
              <div>
                <h4 className="mb-0" style={{ color: '#1e293b' }}>Well-Architected Review</h4>
                <small className="text-muted">{report.accountAlias} • {report.accountId}</small>
              </div>
              <Button 
                onClick={handleExportPDF} 
                variant="outline-primary" 
                size="sm"
                disabled={isExporting}
                style={{ borderColor: '#342ead', color: '#342ead' }}
              >
                <FaFilePdf className="me-2" />
                {isExporting ? 'Exporting...' : 'Export PDF'}
              </Button>
            </div>
            <div className="text-end">
              <div style={{ fontSize: '32px', fontWeight: '700', color: getScoreColor(executiveSnapshot.overallScore) }}>
                {executiveSnapshot.overallScore || 0}
              </div>
              <Badge bg="secondary" style={{ fontSize: '11px' }}>{executiveSnapshot.maturityLevel || 'N/A'}</Badge>
            </div>
          </div>
          
          {/* Tab Navigation */}
          <div className="mb-3">
            <ButtonGroup>
              <Button
                variant={activeTab === 'overview' ? 'primary' : 'outline-secondary'}
                onClick={() => setActiveTab('overview')}
                style={activeTab === 'overview' ? { backgroundColor: '#342ead', borderColor: '#342ead' } : { color: '#495057' }}
              >
                Overview
              </Button>
              <Button
                variant={activeTab === 'details' ? 'primary' : 'outline-secondary'}
                onClick={() => setActiveTab('details')}
                style={activeTab === 'details' ? { backgroundColor: '#342ead', borderColor: '#342ead' } : { color: '#495057' }}
              >
                Details
              </Button>
              <Button
                variant={activeTab === 'findings' ? 'primary' : 'outline-secondary'}
                onClick={() => setActiveTab('findings')}
                style={activeTab === 'findings' ? { backgroundColor: '#342ead', borderColor: '#342ead' } : { color: '#495057' }}
              >
                Findings Register
              </Button>
            </ButtonGroup>
          </div>
        </div>

        {/* Content */}
        <div>
          {activeTab === 'overview' && (
            <OverviewTab agg={agg} getScoreColor={getScoreColor} getPillarIcon={getPillarIcon} />
          )}
          {activeTab === 'details' && (
            <DetailsTab agg={agg} getScoreColor={getScoreColor} getPillarIcon={getPillarIcon} />
          )}
          {activeTab === 'findings' && (
            <FindingsTab agg={agg} />
          )}
        </div>
      </div>
      

    </div>
  );
}

function OverviewTab({ agg, getScoreColor, getPillarIcon }) {
  // Safety checks for required data
  const findingsRegister = agg.findingsRegister || [];
  const executiveSnapshot = agg.executiveSnapshot || {};
  const pillarSummary = executiveSnapshot.pillarSummary || [];
  const topRisks = executiveSnapshot.topRisks || [];
  
  return (
    <>
      {/* KPI Ribbon */}
      <Row className="mb-4">
        <Col md={2}>
          <Card style={{ background: 'rgba(255, 255, 255, 0.95)', borderRadius: '12px', border: '1px solid #e9ecef' }}>
            <Card.Body className="text-center">
              <small className="text-muted d-block mb-2">Overall Score</small>
              <h2 style={{ color: getScoreColor(executiveSnapshot.overallScore), marginBottom: 0 }}>
                {executiveSnapshot.overallScore || 0}
              </h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card style={{ background: 'rgba(255, 255, 255, 0.95)', borderRadius: '12px', border: '1px solid #e9ecef' }}>
            <Card.Body className="text-center">
              <small className="text-muted d-block mb-2">Maturity Level</small>
              <h5 className="mb-0">{executiveSnapshot.maturityLevel || 'N/A'}</h5>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card style={{ background: 'rgba(255, 255, 255, 0.95)', borderRadius: '12px', border: '1px solid #e9ecef' }}>
            <Card.Body className="text-center">
              <small className="text-muted d-block mb-2">Total Findings</small>
              <h2 className="mb-0">{findingsRegister.length}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card style={{ background: 'rgba(255, 255, 255, 0.95)', borderRadius: '12px', border: '1px solid #e9ecef' }}>
            <Card.Body className="text-center">
              <small className="text-muted d-block mb-2">Critical/High</small>
              <h2 className="mb-0 text-danger">
                {findingsRegister.filter(f => ['CRITICAL', 'HIGH'].includes(f.severity)).length}
              </h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card style={{ background: 'rgba(255, 255, 255, 0.95)', borderRadius: '12px', border: '1px solid #e9ecef' }}>
            <Card.Body className="text-center">
              <small className="text-muted d-block mb-2">Generated</small>
              <small className="mb-0">{agg.generatedAt ? new Date(agg.generatedAt).toLocaleDateString() : 'N/A'}</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Assessment Summary */}
      <Card className="mb-4" style={{ background: 'rgba(255, 255, 255, 0.95)', borderRadius: '12px', border: '1px solid #e9ecef' }}>
        <Card.Body>
          <h5 className="mb-3" style={{ color: '#1e293b' }}>Assessment Summary</h5>
          <p style={{ lineHeight: '1.8', color: '#475569' }}>{executiveSnapshot.executiveSummary || 'No summary available.'}</p>
        </Card.Body>
      </Card>

      {/* Pillar Scorecards */}
      <h5 className="mb-3" style={{ color: '#1e293b' }}>Pillar Performance</h5>
      <Row className="mb-4">
        {pillarSummary.map(pillar => (
          <Col md={4} key={pillar.pillar} className="mb-3">
            <Card style={{ 
              background: 'rgba(255, 255, 255, 0.95)', 
              borderRadius: '12px', 
              border: '1px solid #e9ecef',
              cursor: 'pointer',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = '#342ead'}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e9ecef'}>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <span style={{ fontSize: '20px', color: '#342ead' }}>{getPillarIcon(pillar.pillar)}</span>
                    <strong style={{ fontSize: '14px' }}>{pillar.pillar}</strong>
                  </div>
                  <Badge 
                    style={{ 
                      backgroundColor: getScoreColor(pillar.score),
                      fontSize: '14px',
                      padding: '6px 12px'
                    }}>
                    {pillar.score}
                  </Badge>
                </div>
                <div style={{ height: '8px', marginBottom: '12px', backgroundColor: '#e9ecef', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ 
                    width: `${pillar.score}%`, 
                    height: '100%', 
                    backgroundColor: getScoreColor(pillar.score),
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <small className="text-muted">{pillar.summary}</small>
                {pillar.keyRisks && pillar.keyRisks.length > 0 && (
                  <div className="mt-2">
                    <small className="text-muted d-block mb-2">Key Risks:</small>
                    <div className="d-flex flex-column gap-1">
                      {pillar.keyRisks.slice(0, 2).map((risk, idx) => (
                        <div 
                          key={idx} 
                          style={{ 
                            backgroundColor: '#f8f9fa',
                            padding: '6px 10px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            color: '#495057'
                          }}
                        >
                          {risk}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Risks and Strengths Side by Side */}
      <Row className="mb-4">
        <Col md={6}>
          <h5 className="mb-3" style={{ color: '#1e293b' }}>Top Risks Requiring Attention</h5>
          {topRisks.slice(0, 5).map((risk, idx) => (
            <Card key={idx} className="mb-3" style={{ background: 'rgba(255, 255, 255, 0.95)', borderRadius: '12px', border: '1px solid #e9ecef' }}>
              <Card.Body>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                  <span style={{ 
                    backgroundColor: risk.severity === 'CRITICAL' ? '#dc3545' : '#ffc107',
                    color: 'white',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '600',
                    display: 'inline-block',
                    whiteSpace: 'nowrap'
                  }}>
                    {risk.severity}
                  </span>
                  <span style={{ 
                    backgroundColor: '#f8f9fa',
                    color: '#212529',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500',
                    display: 'inline-block',
                    whiteSpace: 'nowrap'
                  }}>
                    {risk.pillar}
                  </span>
                </div>
                <h6 style={{ color: '#1e293b' }}>{risk.title}</h6>
                <p className="mb-2" style={{ color: '#64748b' }}><strong>Impact:</strong> {risk.impact}</p>
                <p className="mb-0" style={{ color: '#64748b', fontSize: '14px' }}>
                  <strong>Remediation:</strong> {risk.remediation}
                </p>
              </Card.Body>
            </Card>
          ))}
        </Col>
        <Col md={6}>
          {pillarSummary.some(p => p.keyStrengths && p.keyStrengths.length > 0) && (
            <>
              <h5 className="mb-3" style={{ color: '#1e293b' }}>Key Strengths</h5>
              {pillarSummary
                .filter(p => p.keyStrengths && p.keyStrengths.length > 0)
                .map((pillar, idx) => (
                  <Card key={idx} className="mb-3" style={{ background: 'rgba(255, 255, 255, 0.95)', borderRadius: '12px', border: '1px solid #e9ecef' }}>
                    <Card.Body>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                        <span style={{ 
                          backgroundColor: '#28a745',
                          color: 'white',
                          padding: '4px 10px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600',
                          display: 'inline-block',
                          whiteSpace: 'nowrap'
                        }}>
                          STRENGTH
                        </span>
                        <span style={{ 
                          backgroundColor: '#f8f9fa',
                          color: '#212529',
                          padding: '4px 10px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500',
                          display: 'inline-block',
                          whiteSpace: 'nowrap'
                        }}>
                          {pillar.pillar}
                        </span>
                      </div>
                      <ul style={{ marginBottom: 0, paddingLeft: '20px' }}>
                        {pillar.keyStrengths.map((strength, sIdx) => (
                          <li key={sIdx} style={{ color: '#475569', marginBottom: '8px' }}>{strength}</li>
                        ))}
                      </ul>
                    </Card.Body>
                  </Card>
                ))
              }
            </>
          )}
        </Col>
      </Row>


    </>
  );
}

function DetailsTab({ agg, getScoreColor, getPillarIcon }) {
  const pillars = agg.pillars || {};
  const pillarKeys = Object.keys(pillars);
  const [selectedPillar, setSelectedPillar] = useState(pillarKeys[0] || '');
  const [expandedControls, setExpandedControls] = useState({});
  const pillarData = pillars[selectedPillar] || { controls: [], summary: '', overallScore: 0 };

  return (
    <>
      {/* Pillar Selector */}
      <div className="mb-4">
        <ButtonGroup>
          {pillarKeys.map(pillar => (
            <Button
              key={pillar}
              size="sm"
              variant={selectedPillar === pillar ? 'primary' : 'outline-secondary'}
              onClick={() => setSelectedPillar(pillar)}
              style={selectedPillar === pillar ? { backgroundColor: '#342ead', border: 'none' } : {}}
            >
              <span className="me-2">{getPillarIcon(pillar)}</span>{pillar}
            </Button>
          ))}
        </ButtonGroup>
      </div>

      {/* Pillar Overview */}
      <Card className="mb-4" style={{ background: 'rgba(255, 255, 255, 0.95)', borderRadius: '12px' }}>
        <Card.Body>
          <Row>
            <Col md={8}>
              <h5>{selectedPillar}</h5>
              <p className="text-muted">{pillarData.summary}</p>
            </Col>
            <Col md={4} className="text-end">
              <h2 style={{ color: getScoreColor(pillarData.overallScore) }}>{pillarData.overallScore}</h2>
              <small className="text-muted">Overall Score</small>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Controls Table */}
      <Card style={{ background: 'rgba(255, 255, 255, 0.95)', borderRadius: '12px' }}>
        <Card.Body>
          <h6 className="mb-3">Controls Assessment</h6>
          <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
            {pillarData.controls.map((control, idx) => (
              <Card key={idx} className="mb-2" style={{ border: '1px solid #e9ecef' }}>
                <Card.Body className="py-3">
                  <Row>
                    <Col md={6}>
                      <div className="d-flex align-items-start gap-2">
                        <Badge 
                          bg={control.status === 'PASS' ? 'success' : control.status === 'PARTIAL' ? 'warning' : control.status === 'UNKNOWN' ? 'secondary' : 'danger'} 
                          text="white" 
                          style={{ fontSize: '10px' }}>
                          {control.status}
                        </Badge>
                        <div>
                          <strong style={{ fontSize: '14px' }}>{control.title}</strong>
                          <div className="text-muted small mt-1">{control.controlId}</div>
                        </div>
                      </div>
                    </Col>
                    <Col md={3}>
                      {control.riskLevel && (
                        <Badge bg="light" text="dark" className="me-1">Risk: {control.riskLevel}</Badge>
                      )}
                    </Col>
                    <Col md={3} className="text-end">
                      {control.affectedResources && control.affectedResources.length > 0 && (
                        <small 
                          style={{ cursor: 'pointer', textDecoration: 'underline', color: '#342ead' }}
                          onClick={() => setExpandedControls(prev => ({ ...prev, [control.controlId]: !prev[control.controlId] }))}
                        >
                          {control.affectedResources.length} {control.affectedResources.length === 1 ? 'resource' : 'resources'}
                        </small>
                      )}
                    </Col>
                  </Row>
                  {expandedControls[control.controlId] && control.affectedResources && control.affectedResources.length > 0 && (
                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e9ecef' }}>
                      <small className="text-muted d-block mb-2">Affected Resources:</small>
                      <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                        {control.affectedResources.map((resource, rIdx) => (
                          <div key={rIdx} style={{ 
                            fontSize: '11px', 
                            padding: '4px 8px', 
                            marginBottom: '4px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '4px',
                            fontFamily: 'monospace'
                          }}>
                            {resource}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {control.findings && control.findings.length > 0 && (
                    <div className="mt-2 pt-2" style={{ borderTop: '1px solid #e9ecef' }}>
                      <small className="text-muted">Findings:</small>
                      {control.findings.map((finding, fIdx) => (
                        <div key={fIdx} className="small text-muted mt-1">• {finding}</div>
                      ))}
                    </div>
                  )}
                  {control.recommendations && control.recommendations.length > 0 && (
                    <div className="mt-2">
                      <small style={{ color: '#342ead' }}>Recommendations:</small>
                      {control.recommendations.slice(0, 2).map((rec, rIdx) => (
                        <div key={rIdx} className="small mt-1">• {rec}</div>
                      ))}
                    </div>
                  )}
                </Card.Body>
              </Card>
            ))}
          </div>
        </Card.Body>
      </Card>
    </>
  );
}

function FindingsTab({ agg }) {
  const [filterSeverity, setFilterSeverity] = useState('ALL');
  const [filterPillar, setFilterPillar] = useState('ALL');
  const [expandedFindings, setExpandedFindings] = useState({});

  const findingsRegister = agg.findingsRegister || [];
  
  const filtered = findingsRegister.filter(f => {
    if (filterSeverity !== 'ALL' && f.severity !== filterSeverity) return false;
    if (filterPillar !== 'ALL' && f.pillar !== filterPillar) return false;
    return true;
  });

  const pillars = [...new Set(findingsRegister.map(f => f.pillar))];

  return (
    <>
      {/* Filters */}
      <Row className="mb-4">
        <Col md={6}>
          <Form.Group>
            <Form.Label className="small text-muted">Filter by Severity</Form.Label>
            <Form.Select size="sm" value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)}>
              <option value="ALL">All Severities</option>
              <option value="CRITICAL">Critical</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label className="small text-muted">Filter by Pillar</Form.Label>
            <Form.Select size="sm" value={filterPillar} onChange={(e) => setFilterPillar(e.target.value)}>
              <option value="ALL">All Pillars</option>
              {pillars.map(p => <option key={p} value={p}>{p}</option>)}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {/* Findings List */}
      <div className="mb-3">
        <small className="text-muted">Showing {filtered.length} of {findingsRegister.length} findings</small>
      </div>

      {filtered.map((finding, idx) => (
        <Card key={idx} className="mb-3" style={{ background: 'rgba(255, 255, 255, 0.95)', borderRadius: '12px', border: '1px solid #e9ecef' }}>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-start">
              <div style={{ flex: 1, minWidth: 0, paddingRight: '15px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px', alignItems: 'center' }}>
                  <span style={{ 
                    backgroundColor: '#0dcaf0', 
                    color: 'white', 
                    padding: '4px 8px', 
                    borderRadius: '4px', 
                    fontSize: '12px',
                    display: 'inline-block'
                  }}>{finding.id}</span>
                  <span style={{ 
                    backgroundColor: '#f8f9fa', 
                    color: '#212529', 
                    padding: '4px 8px', 
                    borderRadius: '4px', 
                    fontSize: '12px',
                    display: 'inline-block'
                  }}>{finding.pillar}</span>
                  <span style={{ 
                    backgroundColor: finding.severity === 'CRITICAL' ? '#dc3545' : finding.severity === 'HIGH' ? '#ffc107' : '#6c757d', 
                    color: 'white', 
                    padding: '4px 8px', 
                    borderRadius: '4px', 
                    fontSize: '12px',
                    display: 'inline-block'
                  }}>{finding.severity}</span>
                </div>
                <h6>{finding.title}</h6>
                <small className="text-muted">Control: {finding.controlId}</small>
              </div>
              <div className="text-end" style={{ flexShrink: 0, minWidth: '120px' }}>
                <Badge bg="warning" text="white">{finding.status}</Badge>
                {finding.affectedResources && finding.affectedResources.length > 0 && (
                  <div className="mt-2">
                    <small 
                      style={{ cursor: 'pointer', textDecoration: 'underline', color: '#342ead' }}
                      onClick={() => setExpandedFindings(prev => ({ ...prev, [finding.id]: !prev[finding.id] }))}
                    >
                      {finding.affectedResources.length} affected resources
                    </small>
                  </div>
                )}
              </div>
            </div>
            {expandedFindings[finding.id] && finding.affectedResources && finding.affectedResources.length > 0 && (
              <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e9ecef' }}>
                <small className="text-muted d-block mb-2">Affected Resources:</small>
                <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                  {finding.affectedResources.map((resource, rIdx) => (
                    <div key={rIdx} style={{ 
                      fontSize: '11px', 
                      padding: '4px 8px', 
                      marginBottom: '4px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px',
                      fontFamily: 'monospace'
                    }}>
                      {resource}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {finding.recommendations && finding.recommendations.length > 0 && (
              <div className="mt-3 pt-3" style={{ borderTop: '1px solid #e9ecef' }}>
                <small className="d-block mb-2" style={{ color: '#342ead' }}>Recommended Actions:</small>
                {finding.recommendations.map((rec, rIdx) => (
                  <div key={rIdx} className="small mb-1">• {rec}</div>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>
      ))}
    </>
  );
}

export default ReportDetailView;
